import { useMemo } from 'react';
import { Cell, Pie, PieChart as RechartsPieChart } from 'recharts';
import { Icon, IconType } from '@/ui';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { ChartNoData } from '@/components/ui/chart-no-data';
import { CHART_COLORS } from '@/lib/chart-utils';
import type { TopOrganization } from '@/services/Dashboard/dashboard.service';
import styles from '../styles.module.css';
import { useTranslation } from 'react-i18next';

export type PieChartDataSegment = {
  name: string;
  value: number;
  fill: string;
  ring: 'outer' | 'inner';
};

type Props = {
  icon?: IconType;
  outerData?: number[];
  innerData?: number[];
  topItems?: TopOrganization[];
  formatOuterValue?: (value: number, index: number) => string;
  formatInnerValue?: (value: number, index: number) => string;
};

function PieChart({
  icon,
  outerData,
  innerData,
  topItems,
  formatOuterValue = (v) => v.toLocaleString(),
  formatInnerValue = (v) => v.toLocaleString(),
}: Props) {
  const { t } = useTranslation();
  const isEmpty =
    !outerData?.length ||
    outerData.every((v) => v === 0) ||
    !innerData?.length ||
    innerData.every((v) => v === 0);

  const count =
    Math.max(outerData?.length ?? 0, innerData?.length ?? 0, topItems?.length ?? 0) || 0;

  const innerRingData = useMemo<PieChartDataSegment[]>(() => {
    if (count === 0) return [];
    return Array.from({ length: count }, (_, i) => ({
      name: topItems?.[i]?.companyName ?? `Item ${i + 1}`,
      value: innerData?.[i] ?? 0,
      fill: CHART_COLORS[i % CHART_COLORS.length],
      ring: 'inner' as const,
    }));
  }, [count, innerData, topItems]);

  const outerRingData = useMemo<PieChartDataSegment[]>(() => {
    if (count === 0) return [];
    return Array.from({ length: count }, (_, i) => ({
      name: topItems?.[i]?.companyName ?? `Item ${i + 1}`,
      value: outerData?.[i] ?? 0,
      fill: CHART_COLORS[i % CHART_COLORS.length],
      ring: 'outer' as const,
    }));
  }, [count, outerData, topItems]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const config: ChartConfig = { value: { label: 'Value' } };
    outerRingData.forEach((item) => {
      config[item.name] = { label: item.name, color: item.fill };
    });
    return config;
  }, [outerRingData]);

  if (isEmpty) {
    return (
      <div className={styles.pieChartContainer}>
        <ChartNoData variant='donut' message={t('Companies.no_data')} />
        {icon != null && (
          <div className={styles.pieChartSvg}>
            <Icon type={icon} fill='transparent' width={56} height={56} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.pieChartContainer}>
      <ChartContainer config={chartConfig} className='aspect-square max-h-[208px] w-[208px]'>
        <RechartsPieChart>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                formatter={(value, name, item) => {
                  const segment = item?.payload as PieChartDataSegment | undefined;
                  const index =
                    segment?.ring === 'outer'
                      ? outerRingData.findIndex((d) => d.name === name)
                      : innerRingData.findIndex((d) => d.name === name);
                  const formatted =
                    segment?.ring === 'outer'
                      ? formatOuterValue(Number(value), index)
                      : formatInnerValue(Number(value), index);
                  return (
                    <div className='flex items-center justify-between gap-4'>
                      <span className='text-muted-foreground'>{name}</span>
                      <span className='font-medium'>{formatted}</span>
                    </div>
                  );
                }}
              />
            }
          />
          {/* Inner ring: transaction count */}
          <Pie
            data={innerRingData}
            dataKey='value'
            nameKey='name'
            innerRadius={50}
            outerRadius={70}
            strokeWidth={2}
            stroke='hsl(var(--card))'
          >
            {innerRingData.map((entry, index) => (
              <Cell key={`inner-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          {/* Outer ring: payment volume */}
          <Pie
            data={outerRingData}
            dataKey='value'
            nameKey='name'
            innerRadius={75}
            outerRadius={95}
            strokeWidth={2}
            stroke='hsl(var(--card))'
          >
            {outerRingData.map((entry, index) => (
              <Cell key={`outer-${index}`} fill={entry.fill} />
            ))}
          </Pie>
        </RechartsPieChart>
      </ChartContainer>
      {icon != null && (
        <div className={styles.pieChartSvg}>
          <Icon type={icon} fill='transparent' width={56} height={56} />
        </div>
      )}
    </div>
  );
}

export default PieChart;
